import { Trans, useLingui } from '@lingui/react/macro'
import { withLayouts } from 'react-dx'
import { getEntries } from '@duiyun/strapi-v4'

import DefaultLayout from '../_layout'
import RootLayout from '../_layout/layouts/RootLayout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import SiteSpecificContextLayout from '../_layout/layouts/SiteSpecificContextLayout'

import type { IEntry } from '@duiyun/strapi-v4'
import type { IReviewProps } from '@/pages/tad.ai/[...params]/_types/review'
import type { InferGetServerSidePropsType, NextPage } from 'next'

import { ALL_LANGUAGES } from '@/components/LanguageSwitcher/data'
import style from '@/pages/tad.ai/_components/H2/index.module.scss'
import { getServerSideData } from '@/helpers/server-data'
import LazyImage from '@/components/LazyImage'
import { GradientBorderCard } from '@/pages/tad.ai/_components/GradientBorderCard'

import { cls } from 'tagged-classnames-free';
import Link from '@/components/Link';
const Page: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ data }) => {
  return (
    <div className='md:pt-30 mx-auto max-w-[1580px] px-4 py-40'>
      <header className='mb-10 md:mb-14'>
        <h1
          className={cls`mx-auto ${style.heightLightText} max-w-[1000px] text-center text-[40px] leading-tight md:text-[56px] lg:leading-snug`}
        >
          <Trans>AI Music Generator Reviews</Trans>
        </h1>
        <p className='text-f-other-10 mx-auto max-w-[720px] pt-4 text-center text-xl'>
          <Trans>
            Explore comprehensive reviews of top AI music generators and elevate
            your music creation experience effortlessly.
          </Trans>
        </p>
      </header>

      {/* cards */}
      <section className='mt-6 grid grid-cols-1 gap-5 md:mt-10 md:grid-cols-3 2xl:grid-cols-5'>
        {data.map((item) => (
          <Link href={item.attributes.url} key={item.id}>
            <GradientBorderCard>
              <LazyImage
                src={item.attributes.icon}
                alt={item.attributes.title}
                width={48}
                height={48}
                className='size-12 rounded-lg object-cover object-center'
              />
              <h2 className='text-sm font-medium leading-snug text-white md:text-base'>
                {item.attributes.h1}
              </h2>
            </GradientBorderCard>
          </Link>
        ))}
      </section>
    </div>
  )
}

const LayoutsPage = withLayouts(Page, [
  (props) => {
    return (
      <DefaultLayout
        {...props}
        main={{
          className: 'bg-black',
          includingHeader: true,
        }}
      />
    )
  },
  (props) => {
    const { t } = useLingui()
    return (
      <RootLayout
        {...props}
        seo={{
          title: t`AI Music Generator Reviews | Tad AI`,
          description: t`Discover the best AI music generators with Tad AI’s in-depth reviews. Unleash your creativity and find the perfect tool for your musical projects today!`,
        }}
      />
    )
  },
  (props) => {
    return <SiteSpecificContextLayout {...props} />
  },
  AntdLayout,
])

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData({ cacheControl: true })(
  async (hostInfo, ctx) => {
    const locale = ctx?.locale || 'en'
    const langObj = ALL_LANGUAGES.find((e) => e.abbr === locale) || null

    const entries = await getEntries<IEntry<IReviewProps>>('tad_reviews', {
      pagination: { limit: 50 },
      sort: ['priority:desc'],
      attributes: [
        'title',
        'description',
        'priority',
        'h1',
        'icon',
        'url',
        'discoverTitle',
      ],
      locale: langObj && locale !== 'en' ? langObj.lang : 'en',
      filters: {
        showReviewPage: { eq: true },
      },
    })
    return entries
  },
)
